import { mapState } from 'vuex';
import { debounce } from 'debounce';
import ContractDocs from '../ContractDocs.vue';
import EditContract from '../EditContract/EditContract.vue';
import Documents from '../Tabs/Documents.vue';
import Services from '../Tabs/Services.vue';
import Acts from '../Tabs/Acts.vue';
import Managers from '../Tabs/Managers.vue';
import ExpandableCell from '../ExpandableCell.vue';
import Spinner from '../Spinner.vue';
import Conclusions from '../Tabs/Conclusions.vue';
import Calculations from '../Tabs/Calculations.vue';
import { utils } from '../../helpers/utils';
import { resizer } from '../../helpers/resizerMixin';
import { transcriptColumnTitle } from '../../helpers/transcriptor';
import { takeLatest } from '../../helpers/takeLatest';

// сколько строк договоров дорисовываем за раз
const CONTRACTS_RENDER_CHUNK = 100;

export default {
  name: 'Contracts',
  mixins: [utils, resizer],
  components: {
    ContractDocs,
    Documents,
    Services,
    Acts,
    Managers,
    EditContract,
    ExpandableCell,
    Spinner,
    Conclusions,
    Calculations,
  },
  data() {
    return {
      tabsHeight: 0,
      selectedType: '0',
      showConfirmDialog: false,
      orgToFollow: null,
      types: [
        {
          label: 'Активные',
          value: '2',
        },
        {
          label: 'Все',
          value: '0',
        },
        {
          label: 'Архив',
          value: '1',
        },
      ],
      isLoading: false,
      showTooltipP: false,
      showTooltipD: false,
      showTooltipU: false,
      showTooltipA: false,
      showTooltipZA: false,
      currentContractName: '',
      isActionsShown: 0,
      isEditContractOpen: false,
      tabs: [
        {
          id: 1, // 4
          label: 'Заключения',
          component: 'Conclusions',
        },
        {
          id: 2, // 1
          label: 'Документы',
          component: 'Documents',
        },
        {
          id: 3, // 2
          label: 'Услуги',
          component: 'Services',
        },
        {
          id: 6,
          label: 'расчеты',
          component: 'Calculations',
        },
        {
          id: 4, // 3
          label: 'Оплаты/Акты',
          component: 'Acts',
        },
        {
          id: 5,
          label: 'Менеджеры',
          component: 'Managers',
        },
      ],
      // соответствует tabs[1] и active-tab-index="1" (раньше id был 1 — это заключения)
      activeTab: {
        id: 2,
        label: 'Документы',
        component: 'Documents',
      },
      tableItems: [],
      rerender: false,
      tabLoading: false,
      contractLoading: false,
      searchInput: '',
      searchString: '',
      renderLimit: CONTRACTS_RENDER_CHUNK,
    };
  },
  created() {
    // фильтрация и перерисовка таблицы — не на каждое нажатие клавиши
    this.applySearch = debounce((val) => {
      this.searchString = val;
    }, 200);
  },
  computed: {
    userRank() {
      return this.$store.getters['user/getUserRank'];
    },
    firstTabsRow() {
      return this.tabs.slice(0, 3);
    },
    secondTabsRow() {
      return this.tabs.slice(3, 6);
    },
    companyName() {
      return this.$store.getters['user/getSelectedCompanyName'];
    },
    contracts() {
      if (this.userRank >= 100) return this.$store.getters['contractsData/getContracts'];
      return this.$store.getters['contractsData/getContracts'].filter(
        (i) => i.for_medsafe === 1 || i.medsafe_locked === 1,
      );
      // return this.$store.getters['contractsData/getContracts'];
    },
    filteredContracts() {
      if (!this.searchString.length) {
        return this.contracts;
      }
      return this.contracts.filter((contract) => contract.id_contract.toString().includes(this.searchString)
        || contract.N_contract.includes(this.searchString)
        || contract.name_contract.includes(this.searchString));
    },
    visibleContracts() {
      return this.filteredContracts.slice(0, this.renderLimit);
    },
    hasMoreContracts() {
      return this.filteredContracts.length > this.renderLimit;
    },
    currentContract: {
      get() {
        return this.$store.state.contractsData.currentContract;
      },
      set(id) {
        let newId = id;
        if (id === 0) {
          newId = '0';
        }
        this.$store.commit('contractsData/SET_CURRENT_CONTRACT', `${newId}`);
      },
    },
    services() {
      return this.$store.getters['contractsData/getServices'];
    },
    acts() {
      return this.$store.getters['contractsData/getActs'];
    },
    managers() {
      return this.$store.getters['contractsData/getManagers'];
    },
    ...mapState({
      actsProcessing: (state) => state.contractsData.actsProcessing,
      prcessing: (state) => state.contractsData.processing,
    }),
    calculations() {
      return this.$store.getters['contractsData/getCalculations'];
    },
    selectedSubdoer() {
      return this.$store.state.contractsData.subdoersBinding.subdoer;
    },
    orgsList() {
      return this.$store.getters['user/companiesList'];
    },
    currentOrg() {
      return this.$store.getters['user/getSelectedCompanyId'];
    },
  },
  watch: {
    searchInput(val) {
      this.applySearch(val);
    },
    // новый список (поиск, смена организации/типа) — снова начинаем с первой порции
    filteredContracts() {
      this.renderLimit = CONTRACTS_RENDER_CHUNK;
    },
    // договор выбран извне (например, из дерева) и находится за пределами отрисованного — дорисовываем до него
    currentContract(id) {
      if (!id) return;
      const index = this.filteredContracts.findIndex((contract) => `${contract.id_contract}` === id);
      if (index >= this.renderLimit) {
        this.renderLimit = Math.ceil((index + 1) / CONTRACTS_RENDER_CHUNK) * CONTRACTS_RENDER_CHUNK;
      }
    },
    companyName(val) {
      this.$nextTick(() => {
        this.setTabsHeight();
      });
    },
    // immediate: при первом заходе на страницу список может уже грузиться
    // (запущен при выборе организации) — тогда изменения флага не будет и лоадер не появится
    prcessing: {
      handler(bool) {
        this.isLoading = bool;
      },
      immediate: true,
    },
  },
  mounted() {
    this.selectedType = this.userRank >= 100 ? '0' : '2';
    this.setTabsHeight();
    // миксин ждёт ось ресайза, а не событие (как в AppMain)
    this._startResize = (evt) => {
      evt.preventDefault();
      this.startResize('horizontal');
    };
    this.$refs.resizer.addEventListener('mousedown', this._startResize);
    // this.emitter.on('loadAllActs', this.loadAllActs);
    this.setContractType();
  },
  beforeUnmount() {
    // eslint-disable-next-line no-unused-expressions
    this.$refs.resizer?.removeEventListener('mousedown', this._startResize);
    // eslint-disable-next-line no-unused-expressions
    this._sentinelObserver?.disconnect();
  },
  methods: {
    // function ref сторожевой строки: Vue передаёт элемент при монтировании и null при удалении
    setSentinel(el) {
      if (!this._sentinelObserver) {
        // root по умолчанию учитывает обрезку прокручиваемыми контейнерами таблицы
        this._sentinelObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.renderLimit += CONTRACTS_RENDER_CHUNK;
          }
        });
      }
      if (this._sentinelEl && this._sentinelEl !== el) {
        this._sentinelObserver.unobserve(this._sentinelEl);
      }
      this._sentinelEl = el;
      if (el) this._sentinelObserver.observe(el);
    },
    setContractType() {
      this.$store.commit('contractsData/SET_CONTRACT_TYPE_FOR_LOAD', this.selectedType);
      this.$store.dispatch('contractsData/getContractsList');
    },
    loadContracts() {
      this.$store.dispatch('contractsData/getContractsList');
    },
    followToOrg() {
      this.emitter.emit('selectNewOrg', this.orgToFollow);
    },
    async showChangeOrgDialog(org) {
      if (this.currentOrg === org.id_org) {
        return;
      }
      const orgExistsInList = this.orgsList.find((organization) => organization.id_org === org.id_org);
      if (!orgExistsInList) {
        if (this.userRank > 100) {
          const backendSearch = await this.$store.dispatch('user/getAdditionalCompanies', org.org);
          const newOrg = backendSearch.find((item) => item.id_org === org.id_org);
          if (newOrg) {
            await this.$store.dispatch('user/addOrgIntoOrgsList', newOrg.id_org);
            this.$store.commit('user/SET_NEW_COMPANY', newOrg);
            this.emitter.emit('selectNewOrg', newOrg);
          }
        } else {
          this.emitter.emit('message', `Нет доступа к ${org.org}`);
          return;
        }
      }
      this.showConfirmDialog = true;
      this.orgToFollow = org;
    },
    async loadAllActs() {
      await this.$store.dispatch('contractsData/getActs', 0);
      this.tableItems = this[this.activeTab.component.toLowerCase()];
      this.emitter.emit('allActsLoaded');
    },
    transcriptColumnTitle(str) {
      return transcriptColumnTitle(str);
    },
    async getComponentsData(id) {
      if (this.currentContract) {
        switch (id) {
          case 1:
            await this.$store.dispatch('contractsData/getConclusions', this.currentContract);
            break;
          case 2:
            await this.$store.dispatch('contractsData/getDocuments', this.currentContract);
            break;
          case 3:
            const params = {
              id_contract: this.currentContract,
            };
            if (this.selectedSubdoer) {
              params.id_contract_sub_doer = this.selectedSubdoer;
            }
            await this.$store.dispatch('contractsData/getServices', params);
            break;
          case 4:
            await this.$store.dispatch('contractsData/getActs', this.currentContract);
            break;
          case 5:
            await this.$store.dispatch('contractsData/getAgents', this.currentContract);
            break;
          case 6:
            await this.$store.dispatch('contractsData/getCalculations', this.currentContract);
            break;
          default:
            break;
        }
      }
    },
    setTabsHeight() {
      this.$nextTick(() => {
        const tableHeader = this.$refs.header;
        // if (tableHeader.clientHeight < 50) {
        //   tableHeader.style.height = '50px';
        // }
        const headerHeight = tableHeader.clientHeight;
        this.tabsHeight = headerHeight - 8;
      });
    },
    async onTabSelect(idx) {
      const isActual = takeLatest('contractTabs');
      this.rerender = true;
      this.activeTab = this.tabs[idx];
      await this.getComponentsData(this.activeTab.id);
      if (!isActual()) return;
      this.finishTabLoading();
    },
    async refresh() {
      const isActual = takeLatest('contractTabs');
      this.tabLoading = true;
      await this.getComponentsData(this.activeTab.id);
      if (!isActual()) return;
      this.finishTabLoading();
    },
    // флаги снимает только последний запуск, иначе устаревший ответ погасит лоадер актуального
    finishTabLoading() {
      this.tableItems = this[this.activeTab.component.toLowerCase()];
      this.rerender = false;
      this.tabLoading = false;
      this.contractLoading = false;
    },
    date(str) {
      if (!str || str.indexOf('1900-01-01') !== -1) {
        return '';
      }
      return this.readableDate(str);
    },
    toggleActions(id) {
      this.isActionsShown = id;
    },
    async selectContract(id) {
      if (this.currentContract === `${id}`) {
        // отменяем ожидание вкладок снятого договора
        takeLatest('contractTabs');
        this.rerender = false;
        this.tabLoading = false;
        this.contractLoading = false;
        this.currentContract = '';
        this.$store.commit('contractsData/RESET_CONTRACT_DATA');
        await this.$store.dispatch('employeesData/getLastPatients', { includeServices: false });
        return false;
      }
      const isFirstSelect = !this.currentContract;
      this.currentContract = `${id}`;
      const isActual = takeLatest('contractTabs');
      // лоадер только в правой панели: список договоров остаётся доступным
      this.contractLoading = true;
      // при первом выборе, как и раньше, подгружаем заключения — теперь параллельно с активной вкладкой
      const tabIds = new Set([this.activeTab.id]);
      if (isFirstSelect) tabIds.add(1);
      await Promise.all([...tabIds].map((tabId) => this.getComponentsData(tabId)));
      if (!isActual()) return false;
      this.finishTabLoading();
    },
    addContract() {
      this.currentContract = '';
      this.isEditContractOpen = true;
    },
    editContract(id) {
      this.isEditContractOpen = true;
    },
  },
};
